import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Statistic,
  Row,
  Col,
  Tag,
  Space,
  Button,
  Alert,
  Typography,
  Select,
} from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { adminTreasuryEndpoints, type TreasuryOperations, type PendingTransfer, type ApprovedNotInitiated } from '../api/adminTreasuryEndpoints';

const { Text, Title } = Typography;
const { Option } = Select;

interface TreasuryOperationsViewProps {
  onTransferClick?: (transferId: string) => void;
  onWithdrawalClick?: (withdrawalId: string) => void;
  // S-H2: State preservation
  initialWindow?: number;
  initialStatus?: string;
  initialType?: string;
  initialPage?: number;
  onFilterChange?: (filters: { window?: number; status?: string; type?: string; page?: number }) => void;
}

const TreasuryOperationsView: React.FC<TreasuryOperationsViewProps> = ({
  onTransferClick,
  onWithdrawalClick,
  initialWindow,
  initialStatus, // Reserved for future status filtering
  initialType, // Reserved for future type filtering
  initialPage = 1, // Reserved for future pagination
  onFilterChange,
}) => {
  const [loading, setLoading] = useState(true);
  const [operations, setOperations] = useState<TreasuryOperations>({
    pending_deposits_total_cents: 0,
    pending_payouts_total_cents: 0,
    approved_not_initiated_total_cents: 0,
    expected_net_change_cents: 0,
    pending_deposits: [],
    pending_payouts: [],
    approved_not_initiated: [],
    last_updated_at: new Date().toISOString(),
  });
  const [windowDays, setWindowDays] = useState<number | undefined>(initialWindow);

  // S-H2: Report filter changes to parent
  useEffect(() => {
    if (onFilterChange) {
      onFilterChange({ window: windowDays });
    }
  }, [windowDays, onFilterChange]);

  useEffect(() => {
    fetchOperations();
  }, [windowDays]);

  const fetchOperations = async () => {
    try {
      setLoading(true);
      const data = await adminTreasuryEndpoints.getOperations({ window_days: windowDays });
      setOperations(data);
    } catch (error: any) {
      console.error('Failed to fetch treasury operations:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const formatDateTime = (dateString: string) => {
    return dayjs(dateString).format('MMM D, YYYY h:mm A');
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'orange';
      case 'processing':
        return 'blue';
      case 'succeeded':
        return 'green';
      case 'failed':
        return 'red';
      default:
        return 'default';
    }
  };

  const depositColumns: ColumnsType<PendingTransfer> = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <div>
          <div><Text strong>{record.user_name || 'N/A'}</Text></div>
          <div><Text type="secondary" style={{ fontSize: 12 }}>{record.user_email}</Text></div>
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount_cents',
      key: 'amount',
      render: (cents) => <Text strong>{formatCurrency(cents)}</Text>,
      sorter: (a, b) => a.amount_cents - b.amount_cents,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: 'Provider',
      key: 'provider',
      render: (_, record) => (
        <div>
          <div><Text>{record.provider || 'N/A'}</Text></div>
          {record.provider_transfer_id && (
            <div>
              <Text type="secondary" style={{ fontSize: 11 }} code>
                {record.provider_transfer_id.substring(0, 16)}...
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => formatDateTime(date),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={() => onTransferClick?.(record.id)}
        >
          View Details
        </Button>
      ),
    },
  ];

  const payoutColumns: ColumnsType<PendingTransfer> = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <div>
          <div><Text strong>{record.user_name || 'N/A'}</Text></div>
          <div><Text type="secondary" style={{ fontSize: 12 }}>{record.user_email}</Text></div>
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount_cents',
      key: 'amount',
      render: (cents) => <Text strong>{formatCurrency(cents)}</Text>,
      sorter: (a, b) => a.amount_cents - b.amount_cents,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: 'Withdrawal',
      dataIndex: 'withdrawal_request_id',
      key: 'withdrawal_request_id',
      render: (id) => (
        id ? (
          <Button
            type="link"
            size="small"
            onClick={() => onWithdrawalClick?.(id)}
          >
            View Request
          </Button>
        ) : (
          <Text type="secondary">N/A</Text>
        )
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => formatDateTime(date),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={() => onTransferClick?.(record.id)}
        >
          View Details
        </Button>
      ),
    },
  ];

  const approvedColumns: ColumnsType<ApprovedNotInitiated> = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <div>
          <div><Text strong>{record.user_name}</Text></div>
          <div><Text type="secondary" style={{ fontSize: 12 }}>{record.user_email}</Text></div>
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount_cents',
      key: 'amount',
      render: (cents) => <Text strong>{formatCurrency(cents)}</Text>,
      sorter: (a, b) => a.amount_cents - b.amount_cents,
    },
    {
      title: 'Approved',
      dataIndex: 'approved_at',
      key: 'approved_at',
      render: (date) => formatDateTime(date),
      sorter: (a, b) => dayjs(a.approved_at).unix() - dayjs(b.approved_at).unix(),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={() => onWithdrawalClick?.(record.id)}
        >
          View & Initiate
        </Button>
      ),
    },
  ];

  if (loading && operations.pending_deposits.length === 0 && operations.pending_payouts.length === 0) {
    return (
      <Card style={{ marginTop: 24 }}>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <ReloadOutlined spin style={{ fontSize: 32, color: '#1890ff' }} />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Loading treasury operations...</Text>
          </div>
        </div>
      </Card>
    );
  }

  const netChangePositive = operations.expected_net_change_cents >= 0;

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%', marginTop: 24 }}>
      {/* Summary Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Pending Deposits"
              value={operations.pending_deposits_total_cents}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<ArrowDownOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {operations.pending_deposits.length} deposits
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Pending Payouts"
              value={operations.pending_payouts_total_cents}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<ArrowUpOutlined style={{ color: '#ff4d4f' }} />}
              valueStyle={{ color: '#ff4d4f' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {operations.pending_payouts.length} payouts
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Approved Not Initiated"
              value={operations.approved_not_initiated_total_cents}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {operations.approved_not_initiated.length} withdrawals
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Expected Net Change"
              value={operations.expected_net_change_cents}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={netChangePositive ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
              valueStyle={{ color: netChangePositive ? '#52c41a' : '#ff4d4f' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Deposits - Payouts
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Filters and Refresh */}
      <Card size="small">
        <Space>
          <Text>Time Window:</Text>
          <Select
            style={{ width: 150 }}
            value={windowDays}
            onChange={setWindowDays}
            placeholder="All time"
          >
            <Option value={undefined}>All time</Option>
            <Option value={7}>Last 7 days</Option>
            <Option value={30}>Last 30 days</Option>
            <Option value={60}>Last 60 days</Option>
            <Option value={90}>Last 90 days</Option>
          </Select>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchOperations}
            loading={loading}
          >
            Refresh
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Last updated: {dayjs(operations.last_updated_at).format('MMM D, YYYY h:mm:ss A')}
          </Text>
        </Space>
      </Card>

      {/* Pending Deposits Table */}
      <Card
        title={
          <Space>
            <ArrowDownOutlined style={{ color: '#52c41a' }} />
            <Title level={5} style={{ margin: 0 }}>
              Pending Deposits
            </Title>
            <Tag color="green">{operations.pending_deposits.length}</Tag>
          </Space>
        }
      >
        {operations.pending_deposits.length > 0 ? (
          <Table
            columns={depositColumns}
            dataSource={operations.pending_deposits}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            loading={loading}
            size="small"
          />
        ) : (
          <Alert
            message="No pending deposits"
            description="All deposits have been completed or there are no deposits in progress."
            type="info"
            showIcon
            icon={<CheckCircleOutlined />}
          />
        )}
      </Card>

      {/* Pending Payouts Table */}
      <Card
        title={
          <Space>
            <ArrowUpOutlined style={{ color: '#ff4d4f' }} />
            <Title level={5} style={{ margin: 0 }}>
              Pending Payouts
            </Title>
            <Tag color="red">{operations.pending_payouts.length}</Tag>
          </Space>
        }
      >
        {operations.pending_payouts.length > 0 ? (
          <Table
            columns={payoutColumns}
            dataSource={operations.pending_payouts}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            loading={loading}
            size="small"
          />
        ) : (
          <Alert
            message="No pending payouts"
            description="All payouts have been completed or there are no payouts in progress."
            type="info"
            showIcon
            icon={<CheckCircleOutlined />}
          />
        )}
      </Card>

      {/* Approved Not Initiated Table */}
      {operations.approved_not_initiated.length > 0 && (
        <Card
          title={
            <Space>
              <ClockCircleOutlined style={{ color: '#faad14' }} />
              <Title level={5} style={{ margin: 0 }}>
                Approved But Not Initiated
              </Title>
              <Tag color="orange">{operations.approved_not_initiated.length}</Tag>
            </Space>
          }
        >
          <Alert
            message="Action Required"
            description="These withdrawals have been approved but payouts have not been initiated yet."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Table
            columns={approvedColumns}
            dataSource={operations.approved_not_initiated}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            loading={loading}
            size="small"
          />
        </Card>
      )}
    </Space>
  );
};

export default TreasuryOperationsView;
